import { enableChartsExporting } from '@ecosystem/export-utils';
import { InsightRendererProps } from '@ecosystem/newron-client-collections';
import { FC } from 'react';
import { Provider } from 'react-redux';
import { renderLoading } from '@pages/Loading';
import Store from '../../../state/Store';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { RetailerOverviewCombinedChartInsight } from './Model';
import { useRegisterExport } from '@utils/collectionUtils/hooks/UseRegisterExport';
import { useExportContext } from '@hooks/export/context/ExportContext';
import ExportContextProvider from '@hooks/export/context/ExportContextProvider';
import { ShareComparisonParam } from '@data-visualization/share-comparison-chart/Types';
import { getShareComparisonChart } from '@services/Chart.service';
import {
  constructDummyPeriodForV2YTDAndMATWeekly,
  getIsUsingCollectionLiveData,
} from '@utils/collectionUtils/CollectionUtils';
import PgWarningBanner from '../warning-banner/pg-warning-banner/PgWarningBanner';
import ShareComparisonChart, {
  ShareComparisonChartProps,
} from '@data-visualization/share-comparison-chart/ShareComparisonChart';
import { PerformanceType } from '@const/PerformanceType';
import { useReRenderBubbleChart } from '@components/collections/opportunity-review-bubble-chart/BubbleChartInsightUtils';

enableChartsExporting();

const CombinedChartInsightContainer: FC<InsightRendererProps<RetailerOverviewCombinedChartInsight>> = ({
  insight,
  onLoaded,
  onExportReady,
}) => {
  const { apiParams, factRevenue, volume, collectionDataType } = insight.data;

  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(apiParams.period, collectionDataType);
      const params: ShareComparisonParam = {
        ...apiParams,
        period: isUsingCollectionLiveData
          ? constructDummyPeriodForV2YTDAndMATWeekly(apiParams.period, apiParams.periodType)
          : apiParams.period,
        isUsingCollectionLiveData: isUsingCollectionLiveData,
      };
      return await getShareComparisonChart(params);
    },
    onLoaded: onLoaded,
  });

  const { reRenderBubbleChart } = useReRenderBubbleChart();
  const { chartRefsIsInitialisedStatus } = useExportContext();

  const { multipleChartsExport } = useRegisterExport({
    numberOfChartsToExport: 1,
    onExportReady,
    chartRef: chartRefsIsInitialisedStatus,
    onExportAfter: reRenderBubbleChart,
  });

  if (isLoading || !chartData?.salesRecordsFound) return renderLoading();

  const props: ShareComparisonChartProps = {
    isLoading: isLoading,
    data: chartData,
    isValue: apiParams.saleType === PerformanceType.Value,
    selectedPeriodicity: apiParams.periodType,
    factRevenue: factRevenue,
    onExportReady: multipleChartsExport.exportCallbacks[0].onExportReady,
    volume: volume,
    isUsingCollection: true,
  };

  return <ShareComparisonChart {...props} />;
};

const CombinedChartInsightRenderer: FC<InsightRendererProps<RetailerOverviewCombinedChartInsight>> = (props) => {
  return (
    <Provider store={Store}>
      <ExportContextProvider>
        <PgWarningBanner baseApiParams={props.insight.data.apiParams} collectionDataType={props.insight.dataType}>
          <CombinedChartInsightContainer {...props} />
        </PgWarningBanner>
      </ExportContextProvider>
    </Provider>
  );
};

export default CombinedChartInsightRenderer;
